import React, { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { a2aDeveloperService } from '../../a2a/services/a2aDeveloperService.js';

// StatCard Component
const StatCard = ({ icon, label, value }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        backgroundColor: '#7c3aed',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{color: 'white', fontSize: '24px'}}>{icon}</span>
      </div>
      <div>
        <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>{label}</div>
        <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

// RecentActivity Component
const RecentActivity = ({ activities = [] }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
    padding: '20px'
  }}>
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px'
    }}>
      最近活動
    </h3>
    {activities.length > 0 ? (
      <div>
        {activities.map((activity, index) => (
          <div key={index} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
            borderBottom: index < activities.length - 1 ? '1px solid #e5e7eb' : 'none'
          }}>
            <span style={{color: '#374151'}}>
              {activity.description || `活動 ${index + 1}`}
            </span>
            <span style={{
              color: '#6b7280',
              fontSize: '12px'
            }}>
              {activity.date || new Date().toLocaleDateString('zh-TW')}
            </span>
          </div>
        ))}
      </div>
    ) : (
      <p style={{color: '#6b7280', fontStyle: 'italic'}}>暫無最近活動</p>
    )}
  </div>
);

// AppPerformance Component
const AppPerformance = ({ apps = [] }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
    padding: '20px'
  }}>
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px'
    }}>
      應用表現
    </h3>
    {apps.length > 0 ? (
      <div>
        {apps.map((app, index) => (
          <div key={index} style={{
            padding: '12px 0',
            borderBottom: index < apps.length - 1 ? '1px solid #e5e7eb' : 'none'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#1f2937',
              marginBottom: '4px'
            }}>
              {app.name || `應用 ${index + 1}`}
            </div>
            <div style={{
              display: 'flex',
              gap: '16px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              <span>⬇️ {app.downloads || 0} 下載</span>
              <span>⭐ {app.rating || '0.0'}</span>
              <span>💰 ${app.revenue || '0.00'}</span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p style={{color: '#6b7280', fontStyle: 'italic'}}>暫無應用數據</p>
    )}
  </div>
);

// MainPage Component - Developer-focused version
const MainPage = ({
  currentUser,
  onTabChange
}) => {
  const [stats, setStats] = useState({
    totalApps: 0,
    publishedApps: 0,
    totalDownloads: 0,
    totalRevenue: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [topApps, setTopApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDeveloperData();
  }, [currentUser]);

  const loadDeveloperData = async () => {
    try {
      setLoading(true);
      
      // Load developer stats from currentUser or fetch from service
      setStats({
        totalApps: currentUser?.total_apps || 0,
        publishedApps: currentUser?.published_apps || 0,
        totalDownloads: currentUser?.total_downloads || 0,
        totalRevenue: currentUser?.total_revenue || 0
      });

      // Mock recent activities (replace with actual API call)
      setRecentActivities([
        { description: '應用 "Math Helper" 已發布', date: new Date().toLocaleDateString('zh-TW') },
        { description: '收到新的應用審核', date: new Date(Date.now() - 86400000).toLocaleDateString('zh-TW') },
        { description: 'API 金鑰已更新', date: new Date(Date.now() - 172800000).toLocaleDateString('zh-TW') }
      ]);

      // Mock top apps (replace with actual API call)
      setTopApps([
        { name: 'Math Helper', downloads: 1250, rating: '4.5', revenue: '125.50' },
        { name: 'English Tutor', downloads: 980, rating: '4.2', revenue: '98.00' },
        { name: 'Science Lab', downloads: 750, rating: '4.7', revenue: '75.00' }
      ]);
    } catch (error) {
      console.error('Error loading developer data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '40px', textAlign: 'center'}}>
        <p style={{color: '#6b7280'}}>載入中...</p>
      </div>
    );
  }

  return (
    <div style={{maxWidth: '1200px', margin: '0 auto', padding: '20px'}}>
      {/* Header Section */}
      <div style={{marginBottom: '32px'}}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          歡迎回來，{currentUser?.name || '開發者'}
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          今天是 {new Date().toLocaleDateString('zh-TW')}，讓我們開始開發工作
        </p>
      </div>

      {/* Developer Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <StatCard icon="📱" label="我的應用" value={stats.totalApps} />
        <StatCard icon="✅" label="已發布" value={stats.publishedApps} />
        <StatCard icon="⬇️" label="總下載" value={stats.totalDownloads} />
        <StatCard icon="💰" label="總收入" value={`$${stats.totalRevenue.toFixed(2)}`} />
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <button 
          onClick={() => onTabChange('create-app')}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
        >
          ➕ 建立新應用
        </button>
        
        <button 
          onClick={() => onTabChange('my-apps')}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          📱 我的應用
        </button>
        
        <button 
          onClick={() => onTabChange('api-keys')}
          style={{
            backgroundColor: '#8b5cf6',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#8b5cf6'}
        >
          🔑 API 金鑰
        </button>
        
        <button 
          onClick={() => onTabChange('app-analytics')}
          style={{
            backgroundColor: '#f59e0b',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
        >
          📊 應用分析
        </button>
      </div>

      {/* Recent Activity and App Performance */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <RecentActivity activities={recentActivities} />
        <AppPerformance apps={topApps} />
      </div>
    </div>
  );
};

export default MainPage;
